import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { PREF_KEYS, readPref, writePref } from "@/lib/storage";
import { setActiveLang } from "@/lib/names";
import { ensureFont } from "@/lib/fonts";

/**
 * Interface language + translations. The dictionaries live in lib/dictionaries/{en,ar}.js and are loaded on demand
 * (Phase 56, committee R-5): the public landing page — which has its own bilingual copy — never downloads them, and the
 * signed-in app only loads the active language (English is fetched as a fallback when Arabic is active).
 *
 * `ready` is true once the active language's dictionary is in memory; routes that translate with `t()` render behind
 * <WhenTranslationsReady> (App.js) so no key names ever flash on screen.
 */
const LOADERS = {
  en: () => import(/* webpackChunkName: "dict-en" */ "@/lib/dictionaries/en"),
  ar: () => import(/* webpackChunkName: "dict-ar" */ "@/lib/dictionaries/ar"),
};
const cache = {};
const pending = {};

export function loadDictionary(lang) {
  const key = lang === "ar" ? "ar" : "en";
  if (cache[key]) return Promise.resolve(cache[key]);
  if (!pending[key]) {
    pending[key] = LOADERS[key]().then((m) => { cache[key] = m.default; delete pending[key]; return cache[key]; });
  }
  return pending[key];
}

const LANG_KEY = PREF_KEYS.lang;

/** First-render language: the Arabic landing address (/ar) or ?lang= wins, then the stored preference, then English. */
function initialLang() {
  if (typeof window !== "undefined") {
    if (window.location.pathname === "/ar") return "ar";
    const q = new URLSearchParams(window.location.search).get("lang");
    if (q === "ar" || q === "en") return q;
  }
  return readPref(LANG_KEY) === "ar" ? "ar" : "en";
}

const LanguageContext = createContext({ lang: "en", setLang: () => {}, t: (k) => k, dir: "ltr", isRTL: false, ready: false });

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(initialLang);
  const [dicts, setDicts] = useState(() => ({ ...cache }));
  setActiveLang(lang); // name helpers (lib/names.js) default to the interface language

  useEffect(() => {
    const dir = lang === "ar" ? "rtl" : "ltr";
    document.documentElement.setAttribute("dir", dir);
    document.documentElement.setAttribute("lang", lang);
    writePref(LANG_KEY, lang);
    if (lang === "ar") ensureFont("ar"); // Arabic UI face is loaded on demand (Phase 56: lighter English landing)
  }, [lang]);

  // Fetch the active dictionary (and English as the fallback behind Arabic); ignore results from a superseded language.
  useEffect(() => {
    let alive = true;
    const adopt = (key) => (d) => { if (alive) setDicts((prev) => (prev[key] === d ? prev : { ...prev, [key]: d })); };
    loadDictionary(lang).then(adopt(lang));
    if (lang === "ar") loadDictionary("en").then(adopt("en"));
    return () => { alive = false; };
  }, [lang]);

  const setLang = useCallback((l) => setLangState(l === "ar" ? "ar" : "en"), []);

  const t = useCallback(
    (key, vars) => {
      let s = dicts[lang]?.[key] ?? dicts.en?.[key] ?? key;
      // Interpolated values (names, asset titles, amounts) are wrapped in a first-strong bidi isolate (U+2068 … U+2069) so an
      // English asset name inside an Arabic sentence — or an Arabic name inside an English one — keeps its own reading order.
      if (vars) Object.entries(vars).forEach(([k, v]) => { s = s.replace(new RegExp(`\\{${k}\\}`, "g"), `\u2068${String(v)}\u2069`); });
      return s;
    },
    [lang, dicts]
  );

  const ready = Boolean(dicts[lang]);
  const value = useMemo(() => ({ lang, setLang, t, dir: lang === "ar" ? "rtl" : "ltr", isRTL: lang === "ar", ready }), [lang, setLang, t, ready]);
  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export const useLang = () => useContext(LanguageContext);
